import { Card, Modal } from "antd";
import { CSSProperties, useRef } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import ImageIcon from "assets/icons/imageIcon";
import ClassTag from "../ClassTag";
import EncourageProgress from "views/uikit/ProgressBars/VLEncourageProgress";
import DeleteIcon from "assets/icons/DeleteIcon";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import { useDroppable } from "@dnd-kit/core";
import { getProgressText } from "views/uikit/ProgressBars/VLEncourageProgress/utils";
import { DropzoneIcon } from "assets/icons/DropzoneIcon";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import { ResizableDiv } from "../../../ResizeableDiv/ResizeableDiv";
import ImageItem from "./ImageItem/ImageItem";
import { useCarouselForSeedStepPreviews } from "hooks/LabelPropagation/useCarouselForSeedStepPreviews";
import VLImagesCarouselPreview from "views/uikit/VLCarousel/VLImagesCarouselPreview";
import useLazyLoading from "hooks/useLazyLoading";

export interface ClassCardProps {
  value: string;
  onDelete?: (value: string) => void;
  progressValue?: number;
  min: number;
  isSelected?: boolean;
  items?: CardItem[];
  onItemDelete?: (value: string, mediaId: string) => void;
  classId: string;
  pausedForReview?: boolean;
}

export interface CardItem {
  id: string;
  imgUri: string;
  originUri: string;
}

export const MINIMUM_LENGTH = 8;
const PAGE_SIZE = 12;
const INITIAL_PAGE_SIZE = 12;

export default function ClassCard({
  value,
  onDelete,
  min = 0,
  isSelected = false,
  items,
  progressValue = 0,
  onItemDelete,
  classId,
  pausedForReview,
}: ClassCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate totalItems first
  const totalItems = items?.length || 0;

  // Track overflow status (needed for hook)
  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    containerRef,
    { current: null },
    {
      shouldObserveInnerRef: false,
      dependencyList: [totalItems],
    },
  );

  // Lazy loading hook - start with 12 items to ensure scroll
  // Only trigger loading if scrolling is possible (isOverflowing) AND at bottom
  const shouldLoadMore = isOverflowing && isAtBottom;
  const { displayCount } = useLazyLoading({
    totalItems,
    pageSize: PAGE_SIZE,
    initialPageSize: INITIAL_PAGE_SIZE,
    shouldLoad: shouldLoadMore,
  });

  const {
    isCarouselOpen,
    carouselImages,
    selectedImageIndex,
    handleImageClick,
    handleCarouselChange,
    handleCarouselAction,
    closeCarousel,
  } = useCarouselForSeedStepPreviews({
    classItemsMap: items ? { [classId]: items } : {}, // Pass original items, not visibleItems
    onItemDelete: onItemDelete || (() => {}),
    pausedForReview,
  });

  const progressText = getProgressText(progressValue, min);

  // Display configuration
  const finalDisplayCount = Math.max(displayCount, MINIMUM_LENGTH);
  const hasMoreThanEight = totalItems > MINIMUM_LENGTH;
  const squares = Array.from({ length: finalDisplayCount });

  // Limit items to only what should be displayed (progressive loading)
  const visibleItems = items?.slice(0, finalDisplayCount) || [];
  const { setNodeRef, isOver } = useDroppable({
    id: `dropzone-${classId}`,
    data: { classId },
  });

  const cardClassName = classNames(styles.card);

  const isActive = isOver || isSelected;

  const containerClassnames = classNames(styles.squaresContainer, {
    [styles.resizable]: !isActive && hasMoreThanEight,
    [styles.overflowing]: !isActive && isOverflowing && !isAtBottom,
    [styles.grid]: Boolean(items?.length),
  });

  const resizableWrapperClassnames = classNames(styles.resizableWrapper, {
    [styles.noMarginTop]: pausedForReview,
  });

  const renderSquareContent = (i: number) => {
    if (visibleItems && visibleItems[i]) {
      return (
        <ImageItem
          item={visibleItems[i]}
          index={i}
          classId={classId}
          onImageClick={handleImageClick}
          onItemDelete={pausedForReview ? undefined : onItemDelete}
          items={items || []} // Pass full items array for carousel, not just visibleItems
        />
      );
    }
    return null;
  };

  return (
    <Card
      className={cardClassName}
      title={
        <div className={styles.header}>
          <ClassTag value={value} />
          {onDelete && (
            <div onClick={() => onDelete(classId)}>
              <div aria-label="delete" className={styles.deleteButton}>
                <DeleteIcon variant="new" />
              </div>
            </div>
          )}
        </div>
      }
      variant="outlined"
    >
      {!pausedForReview && (
        <EncourageProgress
          value={progressValue || 0}
          min={min}
          icon={<ImageIcon />}
          text={progressText}
        />
      )}

      <div className={resizableWrapperClassnames} ref={setNodeRef}>
        <ResizableDiv
          minHeight={152}
          ref={containerRef}
          className={containerClassnames}
          isResizeEnabled={hasMoreThanEight}
        >
          {!items?.length || isOver || isSelected ? (
            <Dropzone
              isLoading={isSelected}
              isOver={isOver}
              style={{
                height: items?.length
                  ? containerRef.current?.clientHeight
                  : 152,
                width: 388,
              }}
            />
          ) : (
            <>
              {squares.map((_, i) => {
                return (
                  <div key={i} className={styles.square} data-testid="square">
                    {renderSquareContent(i)}
                  </div>
                );
              })}
            </>
          )}
        </ResizableDiv>
      </div>
      <Modal
        open={isCarouselOpen}
        onCancel={closeCarousel}
        title={value}
        width={`70%`}
        footer={false}
      >
        {carouselImages.length > 0 && (
          <VLImagesCarouselPreview
            imagesList={carouselImages}
            selectedItem={selectedImageIndex}
            onActionClicked={handleCarouselAction}
            afterChange={handleCarouselChange}
          />
        )}
      </Modal>
    </Card>
  );
}

const Dropzone = ({
  isOver,
  isLoading,
  style,
}: {
  style?: CSSProperties;
  isOver?: boolean;
  isLoading?: boolean;
}) => (
  <div
    className={classNames(styles.dropzone, { [styles.over]: isOver })}
    style={style}
  >
    <div>
      <div className={styles.icon}>
        {isLoading ? <VLColorfulLoader /> : <DropzoneIcon />}
      </div>
      <div>
        {isOver
          ? "Drop Samples Here"
          : isLoading
            ? "Adding..."
            : "Select OR Drag Samples to Assign Here"}
      </div>
    </div>
  </div>
);
